import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderService } from '@/services/order.service';
import { OrderItem } from '@bookstore/shared';
import { productService } from '@/services/product.service';
import { toast } from 'sonner';


// Fetch all orders for the current user
export const useUserOrders = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['user-orders', userId],
    queryFn: () => orderService.getUserOrders(userId!),
    enabled: !!userId,
    staleTime: 60 * 1000,
  });
};

// Fetch a single order by id
export const useOrder = (orderId: string | undefined) => {
  return useQuery({
    queryKey: ['order', orderId],
    queryFn: () => orderService.getOrderById(orderId!),
    enabled: !!orderId,
    staleTime: 60 * 1000,
  });
};

// get products in an order
export const useOrderProducts = (orderItems: OrderItem[] | undefined) => {
  return useQuery({
    queryKey: ['order-products', orderItems?.map(item => item.productId).join(',')],
    queryFn: async () => {
      if (!orderItems?.length) return [];
      const ids = orderItems.map(item => item.productId);
      return await productService.getProductsByIds(ids); // Implement this in service: batch getDocs or loop
    },
    enabled: !!orderItems?.length,
    staleTime: 5 * 60 * 1000,
  });
};


// Create order (calls Firebase function)
export const useCreateOrder = () => {
  return useMutation({
    mutationFn: orderService.createOrder,
  });
};

// Verify payment (calls Firebase function)
export const useVerifyPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: orderService.verifyPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-orders'] });
    },
  });
};

// Cancel an order
export const useCancelOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId,  initiatedBy, reason, }: { orderId: string;  initiatedBy: string , reason?: string}) =>
      orderService.cancelOrder(orderId,   initiatedBy, reason ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-orders'] });
      queryClient.invalidateQueries({ queryKey: ['order'] });
      toast.success('Order cancelled successfully.');
    },
    onError: (error) => {
      console.error('Error cancelling order:', error);
      toast.error('Failed to cancel order. Please try again later.');
    }
  });
};